/*
 * Copyright (c) 2024 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
'use client';!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:mta-learning":true});}catch(e){}}();

import { useAtom } from 'jotai'
import { useTranslations } from 'next-intl'
import React, { useEffect, useState } from 'react'
import { MdOutlineCookie } from 'react-icons/md'

import { CookieDialog } from '@/components/cookies/CookieDialog'
import { Button } from '@/components/ui/Button'
import { trackVercelEvent } from '@/lib/analytics/vercel'
import { analyticalConsentAtom } from '@/store/cookies'

/**
 * The cookie consent banner that appears in the bottom left corner of the page. The banner allows the user to customise
 * their cookie preferences. If the user has not made a choice on the cookie consent, the banner will be displayed.
 * Otherwise, a floating cookie icon will be displayed in the bottom left corner of the page.
 */
const CookieBanner: React.FC = () => {
  const t = useTranslations('CookieBanner')
  const [preferencesDialogOpen, setPreferencesDialogOpen] = useState(false)
  const [showCookieBanner, setShowCookieBanner] = useState(false)
  const [analyticalConsent, setAnalyticalConsent] = useAtom(analyticalConsentAtom)

  /**
   * Handle the user's choice on the cookie consent banner.
   *
   * @param decision - the user's choice on overall cookie consent (reject or accept all cookies)
   */
  const handleCookieConsentChoice = async (decision: boolean) => {
    trackVercelEvent('cookie_consent', { choice: decision ? 'accept' : 'reject', source: 'banner' })
    await setAnalyticalConsent(decision)
  }

  /**
   * Handle the user's choice to open the cookie consent dialog to customise their preferences.
   *
   * @param source - the control the user opened the dialog from
   */
  const handleOpenCookieConsentDialog = (source: 'banner' | 'floating_button') => {
    trackVercelEvent('cookie_preferences_opened', { source })
    setPreferencesDialogOpen(true)
  }

  /**
   * Show the cookie banner if the user has not made a choice on the cookie consent.
   */
  useEffect(() => {
    const isUndecided = analyticalConsent === undefined

    setShowCookieBanner(isUndecided)

    if (isUndecided) {
      trackVercelEvent('cookie_banner_shown')
    }
  }, [analyticalConsent])

  return (
    <>
      <CookieDialog open={preferencesDialogOpen} handleClose={() => setPreferencesDialogOpen(false)} />
      {showCookieBanner ? (
        <div className={'fixed bottom-4 left-4 z-max w-[440px] max-w-[calc(100vw-2rem)] sm:bottom-10 sm:left-10'}>
          <div className={'flex flex-col py-4 px-6 bg-white gap-2.5 rounded-md shadow-md drop-shadow-md'}>
            <h2 className={'text-grey-450 text-lg font-bold'}>{t('weValueYourPrivacy')}</h2>
            <p className={'text-sm text-grey-450 leading-6'}>{t('weUseCookiesTo')}</p>
            <div className={'flex flex-row flex-wrap justify-start items-center gap-2'}>
              <Button variant={'grey'} onClick={() => handleOpenCookieConsentDialog('banner')}>
                {t('customiseButtonText')}
              </Button>
              <Button variant={'grey'} onClick={() => handleCookieConsentChoice(false)}>
                {t('rejectAllButtonText')}
              </Button>
              <Button variant={'secondary'} onClick={() => handleCookieConsentChoice(true)}>
                {t('acceptAllButtonText')}
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <button
          type={'button'}
          aria-label={t('customiseButtonText')}
          className={
            'fixed bottom-5 left-5 z-max rounded-full bg-blue-800 p-2 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300 focus-visible:ring-offset-2'
          }
          onClick={() => handleOpenCookieConsentDialog('floating_button')}
        >
          <MdOutlineCookie aria-hidden={'true'} className={'text-white text-4xl'} />
        </button>
      )}
    </>
  )
}

export { CookieBanner }
